import { createContext, useContext, useState } from 'react'

import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium'

// The goal of this context is to manage the visibility of column groups
//   It will track the state of the column group visibility (collapse or not collapse)
//   Afterwards, it will be used to determine which columns should be visible or hidden
export type CollapsibleHeaderContextType = {
  columnGroupVisibility: GridColumnVisibilityModel,
  updateColumnGroupVisibility: (columnGroupId: string, columnGroupFields: string[], visible: boolean, numColumnsWhenCollapsed: number) => void,
  columnVisibilityModel: GridColumnVisibilityModel,
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void
}

export const CollapsibleHeaderContext = createContext<CollapsibleHeaderContextType>({
  columnGroupVisibility: {},
  updateColumnGroupVisibility: () => { },
  columnVisibilityModel: {},
  setColumnVisibilityModel: () => { },
})

// Used to access context state
export const useCollapsibleHeaderContext = () => {
  const context = useContext(CollapsibleHeaderContext)

  if (context === undefined) {
    throw new Error(
      'useCollapsibleHeaderContext must be used within a CollapsibleHeaderContext.Provider',
    )
  }

  return context
}

// Hide all columns in the group except the first two
const getColumnVisibilityFromGroupFields = (groupFields: string[], isVisible: boolean, numColumnsWhenCollapsed: number) => {
  let model: GridColumnVisibilityModel = {}

  groupFields.forEach((field, index) => {
    if (isVisible) {
      model = Object.assign(model, {
        [field]: true,
      })

      return
    }

    model = Object.assign(model, {
      [field]: index < numColumnsWhenCollapsed,
    })
  })

  return model
}

// Used to initialize context state
export const useCollapsibleHeaderState = (initialColumnGroupVisibility?: GridColumnVisibilityModel, initialColumnVisibility?: GridColumnVisibilityModel) => {
  const [columnGroupVisibility, setColumnGroupVisibility] = useState<GridColumnVisibilityModel>(initialColumnGroupVisibility ?? {}) //column group visibility (collapse or not collapse)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(initialColumnVisibility ?? {}) //individual column visibility

  const updateColumnGroupVisibility = (
    columnGroupId: string,
    columnGroupFields: string[],
    visible: boolean,
    numColumnsWhenCollapsed: number = 2,
  ) => {
    setColumnGroupVisibility((prev) => ({
      ...prev,
      [columnGroupId]: visible,
    }))

    setColumnVisibilityModel((prev) => {
      const updatedColumnVisibilityModel = getColumnVisibilityFromGroupFields(columnGroupFields, visible, numColumnsWhenCollapsed)

      return {
        ...prev,
        ...updatedColumnVisibilityModel,
      }
    })
  }

  return {
    columnGroupVisibility,
    updateColumnGroupVisibility,
    columnVisibilityModel,
    setColumnVisibilityModel,
  }
}
